import { type FC, useMemo } from "react";

import { Control, DropdownInput, Option, Radio, RadioGroup, Status } from "@snapchat/snap-design-system";
import { Field, FieldArray, type FieldProps, useFormikContext } from "formik";
import { useIntl } from "react-intl";
import { object, string } from "yup";

import { HttpRequestMethod, type TargetPlatform } from "../../../../gql-generated/types";
import { FormikInput, FormikReadOnlyInput } from "../../../../shared/formik";
import { fieldErrorCreator } from "../../../../shared/formik/fieldErrorCreator";
import { TagsInput } from "../../../../shared/formik/tags-input";
import { onlyCameraKitSelected } from "../../../utils/onlyCameraKitSelected";
import {
    CustomProcessorLabel,
    CustomProcessorTooltip,
    CustomVisibilityUserListButtonCta,
    CustomVisibilityUserListLabel,
    CustomVisibilityUserListTooltip,
    DescriptionLabel,
    DescriptionTooltip,
    DocumentationUrlLabel,
    DocumentationUrlTooltip,
    HostLabel,
    HostTooltip,
    IconUrlLabel,
    IconUrlTooltip,
    MaxRequestSizeLabel,
    MaxResponseSizeLabel,
    MaxResponseTimeLabel,
    MinLensStudioVersionLabel,
    MinLensStudioVersionTooltip,
    NameLabel,
    NameTooltip,
    ProcessorLabel,
    ProvidedProcessorLabel,
    ProvidedProcessorTooltip,
    ProviderLabel,
    ProviderTooltip,
    SnapkitAppIdLabel,
    SnapkitAppIdTooltip,
    TargetPlatformsLabel,
    TargetPlatformsUpdateTooltip,
    VisibilityLabel,
    VisibilityTooltip,
    messages,
} from "../../messages.generated";
import { type RemoteApiFormKeys, type RemoteApiFormValues, validTargetPlatforms } from "../schema/remoteApiSpecSchema";

import { Divider, controlStyle, httpProcessorLabel, inputStyle } from "./ApiEdit.styles";

interface ApiEditProps {
    isUpdate?: boolean;
}

export const ApiEdit: FC<ApiEditProps> = ({ isUpdate }) => {
    const { values, errors, touched } = useFormikContext<RemoteApiFormValues>();

    const makeFieldError = useMemo(
        () => fieldErrorCreator<RemoteApiFormValues, RemoteApiFormKeys>(errors, touched),
        [errors, touched],
    );

    const intl = useIntl();
    return (
        <>
            <Field
                className={inputStyle}
                name="name"
                id="name"
                component={FormikInput}
                label={<NameLabel />}
                tooltip={<NameTooltip />}
                status={makeFieldError("name")}
                statusType={makeFieldError("name") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="providerName"
                id="providerName"
                component={FormikInput}
                label={<ProviderLabel />}
                tooltip={<ProviderTooltip />}
                status={makeFieldError("providerName")}
                statusType={makeFieldError("providerName") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="documentationUrl"
                id="documentationUrl"
                component={FormikInput}
                label={<DocumentationUrlLabel />}
                tooltip={<DocumentationUrlTooltip />}
                status={makeFieldError("documentationUrl")}
                statusType={makeFieldError("documentationUrl") && Status.ERROR}
                Url
            />

            <Field
                name="description"
                className={inputStyle}
                id="description"
                component={FormikInput}
                label={<DescriptionLabel />}
                tooltip={<DescriptionTooltip />}
                placeholder={intl.formatMessage(messages.descriptionPlaceholder)}
                status={makeFieldError("description")}
                statusType={makeFieldError("description") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="iconUrl"
                id="iconUrl"
                component={FormikInput}
                label={<IconUrlLabel />}
                tooltip={<IconUrlTooltip />}
                status={makeFieldError("iconUrl")}
                statusType={makeFieldError("iconUrl") && Status.ERROR}
            />

            <Field
                className={controlStyle}
                name="targetPlatforms"
                id="targetPlatforms"
                status={makeFieldError("targetPlatforms")}
                statusType={makeFieldError("targetPlatforms") && Status.ERROR}
            >
                {({ field, form }: FieldProps<(typeof validTargetPlatforms)[]>) => {
                    return (
                        <DropdownInput
                            label={<TargetPlatformsLabel />}
                            tooltip={<TargetPlatformsUpdateTooltip />}
                            placeholder={intl.formatMessage(messages.targetPlatformsSelectPlaceholder)}
                            mode="multiple"
                            disabled={isUpdate}
                            {...field}
                            onChange={(value) => {
                                void form.setFieldValue("targetPlatforms", value);

                                // Reset CameraKit only fields
                                if (!onlyCameraKitSelected(value as TargetPlatform[])) {
                                    void form.setFieldValue("host", "");
                                    void form.setFieldValue("useBuiltinLocalProcessor", true);
                                    void form.setFieldValue("snapkitAppId", "");
                                    void form.setFieldValue("customVisibilityUsersList", []);
                                    void form.setFieldValue(
                                        "endpoints",
                                        values.endpoints.map((endpoint) => ({
                                            ...endpoint,
                                            methods: [HttpRequestMethod.Get],
                                        })),
                                    );
                                }
                            }}
                            onBlur={() => {
                                void form.setFieldTouched("targetPlatforms", true);
                            }}
                        >
                            {validTargetPlatforms.map((platform) => (
                                <Option key={platform} value={platform}>
                                    {platform}
                                </Option>
                            ))}
                        </DropdownInput>
                    );
                }}
            </Field>

            <Field
                className={controlStyle}
                name="visibility"
                id="visibility"
                component={FormikReadOnlyInput}
                label={<VisibilityLabel />}
                tooltip={<VisibilityTooltip />}
                status={makeFieldError("visibility")}
                statusType={makeFieldError("visibility") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="minLensStudioVersion"
                id="minLensStudioVersion"
                component={FormikInput}
                label={<MinLensStudioVersionLabel />}
                tooltip={<MinLensStudioVersionTooltip />}
                status={makeFieldError("minLensStudioVersion")}
                statusType={makeFieldError("minLensStudioVersion") && Status.ERROR}
            />

            <Divider />

            <FieldArray name="customVisibilityUsersList">
                {({ remove, push }) => (
                    <TagsInput
                        validationSchema={object({
                            item: string().required(),
                        })}
                        label={<CustomVisibilityUserListLabel />}
                        tooltip={<CustomVisibilityUserListTooltip />}
                        items={values.customVisibilityUsersList ?? []}
                        onAddItem={(item) => {
                            const items = values.customVisibilityUsersList ?? [];
                            // dedupe
                            if (items.includes(item)) {
                                return;
                            }

                            push(item);
                        }}
                        onRemoveItem={(item) => {
                            const index = (values.customVisibilityUsersList ?? []).indexOf(item);
                            remove(index);
                        }}
                        buttonCta={<CustomVisibilityUserListButtonCta />}
                        disabled={!onlyCameraKitSelected(values.targetPlatforms)}
                    />
                )}
            </FieldArray>

            <Field name="useBuiltinLocalProcessor">
                {({ field: { value }, form }: FieldProps<boolean>) => {
                    return (
                        <Control className={controlStyle} label={<ProcessorLabel />}>
                            <RadioGroup
                                disabled={!onlyCameraKitSelected(values.targetPlatforms)}
                                value={value}
                                onChange={(val) => {
                                    if (val !== value) {
                                        void form.setFieldValue("useBuiltinLocalProcessor", val);
                                    }
                                }}
                            >
                                <Radio
                                    label={
                                        <div className={httpProcessorLabel}>
                                            <ProvidedProcessorLabel />
                                            <p>
                                                <ProvidedProcessorTooltip />
                                            </p>
                                        </div>
                                    }
                                    value={true}
                                />
                                <Radio
                                    label={
                                        <div className={httpProcessorLabel}>
                                            <CustomProcessorLabel />
                                            <p>
                                                <CustomProcessorTooltip />
                                            </p>
                                        </div>
                                    }
                                    value={false}
                                />
                            </RadioGroup>
                        </Control>
                    );
                }}
            </Field>

            <Field
                className={inputStyle}
                name="snapkitAppId"
                id="snapkitAppId"
                component={FormikInput}
                label={<SnapkitAppIdLabel />}
                tooltip={<SnapkitAppIdTooltip />}
                status={makeFieldError("snapkitAppId")}
                statusType={makeFieldError("snapkitAppId") && Status.ERROR}
                disabled={!onlyCameraKitSelected(values.targetPlatforms)}
            />

            <Field
                className={inputStyle}
                name="host"
                id="host"
                component={FormikInput}
                label={<HostLabel />}
                tooltip={<HostTooltip />}
                status={makeFieldError("host")}
                statusType={makeFieldError("host") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="maxRequestSizeBytes"
                id="maxRequestSizeBytes"
                type="number"
                component={FormikInput}
                label={<MaxRequestSizeLabel />}
                status={makeFieldError("maxRequestSizeBytes")}
                statusType={makeFieldError("maxRequestSizeBytes") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="maxResponseSizeBytes"
                id="maxResponseSizeBytes"
                type="number"
                component={FormikInput}
                label={<MaxResponseSizeLabel />}
                status={makeFieldError("maxResponseSizeBytes")}
                statusType={makeFieldError("maxResponseSizeBytes") && Status.ERROR}
            />

            <Field
                className={inputStyle}
                name="maxResponseTimeMillis"
                id="maxResponseTimeMillis"
                type="number"
                component={FormikInput}
                label={<MaxResponseTimeLabel />}
                status={makeFieldError("maxResponseTimeMillis")}
                statusType={makeFieldError("maxResponseTimeMillis") && Status.ERROR}
            />
        </>
    );
};
